import {SourceViewCollapseTemplate} from '../../html/components/source-view-collapse.html'

import Component from '../shared/component'
import visit from '../shared/visit'

import {HighlightContextMixin} from './highlight'

const Space = ' '
const keyCodes = [
  'ArrowUp',
  'ArrowRight',
  'ArrowDown',
  'ArrowLeft',
  'Home',
  'Enter',
  'Space',
  Space,
  // IE/Edge dialect:
  'Up',
  'Right',
  'Down',
  'Left',
  'Spacebar'
]

export const SourceView = Component('v-source-view', {
  mixins: [
    HighlightContextMixin
  ],
  props: {
    source: {
      // type: Node
      default: null
    },
    visitors: {
      type: Object,
      default () {
        return {}
      }
    },
    selected: {
      // type: Element
      default: null
    }
  },
  data () {
    const expandMap = new Map()
    this.setExpandedByDefault(expandMap)

    return {
      selectedNode: null,
      collapsibles: new Map(),
      expandMap
    }
  },
  mounted () {
    if (!this.source) {
      return
    }

    this.$nextTick(this.setInitialNode)
  },
  computed: {
    virtual () {
      let {source, visitors} = this

      if (source) {
        return lineNumber(clean(visit(virtualize(source), visitors)))
      }
    },
    currentCollapsible () {
      return this.collapsibles.get(this.selectedNode)
    }
  },
  watch: {
    source () {
      this.setExpandedByDefault(this.expandMap)
      this.setInitialNode()
    },
    index () {
      this.highlight()
    },
    selected (val) {
      // in case the same node is selected again (due to reactive loop)
      // don't expand it
      this.selectNode(val, { expand: elementFromNode(val) !== this.selectedNode })
    },
    selectedNode (val) {
      this.$emit('select', val)
    }
  },
  methods: {
    setInitialNode () {
      if (this.selected) {
        this.selectNode(this.selected, { expand: true })
      } else {
        this.selectNode(this.source.querySelector('.body') || this.source, { focus: false, expand: true })
      }
    },
    setExpandedByDefault (expandMap) {
      if (!this.source) {
        return
      }
      const nodesToExpand = [this.source, ...this.source.querySelectorAll('html, body'), ...this.source.children]
      nodesToExpand.forEach(n => expandMap.set(n, true))
    },
    highlight () {
      for (const [node, highlights] of this.index) {
        const ref = this.refs.get(node)

        if (ref && ref.elm) {
          for (const highlight of highlights) {
            highlight.attach(ref.elm)
          }
        }
      }
    },
    selectNode (node, opts) {
      const defaultOpts = {
        focus: true,
        expand: false
      }
      if (opts === undefined) {
        opts = defaultOpts
      }
      const { focus, expand } = { ...defaultOpts, ...opts }

      if (node) {
        const element = elementFromNode(node)
        if (this.contains(element)) {
          this.selectedNode = element
          if (focus) {
            this.expand(element, { focus, expand })
          }
        }
      }
    },
    expand (element, { focus, expand }) {
      if (element.parentNode && this.contains(element.parentNode)) {
        this.expand(element.parentNode, { focus: false, expand: true })
      }
      expand && this.expandMap.set(element, true)

      if (focus) {
        this.$nextTick(() => {
          const ref = this.refs.get(element)
          if (ref && ref.elm) {
            const collapsible = this.collapsibles.get(element)

            let focusedElement
            if (collapsible) {
              focusedElement = collapsible.focus()
            } else {
              focusedElement = ref.elm
              ref.elm.focus()
            }
            focusedElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' })
          }
        })
      }
    },
    onKeyDown (e) {
      if (keyCodes.includes(e.key)) {
        e.preventDefault()

        const collapsible = this.currentCollapsible
        switch (e.key) {
          case Space:
          case 'Enter':
          case 'Space':
          case 'Spacebar':
            collapsible && collapsible.toggle()
            break
          case 'Home':
            this.selectNode(this.source)
            break
          case 'ArrowLeft':
          case 'Left':
            if (collapsible && collapsible.isExpanded) {
              collapsible.toggle()
            } else {
              this.jumpUp()
            }
            break
          case 'ArrowRight':
          case 'Right':
            if (collapsible && !collapsible.isExpanded) {
              collapsible.toggle()
            } else {
              this.goIn()
            }
            break
          case 'ArrowUp':
          case 'Up':
            this.up()
            break
          case 'ArrowDown':
          case 'Down':
            if (collapsible && collapsible.isExpanded) {
              this.goIn()
            } else {
              this.down()
            }
            break
        }
      }
    },
    jumpUp () {
      this.selectNode(this.selectedNode.parentNode)
    },
    up () {
      this.selectNode(this.lastExpandedChildOrSelv(this.selectedNode.previousElementSibling) || this.selectedNode.parentNode)
    },
    lastExpandedChildOrSelv (element) {
      if (!element) {
        return
      }

      const collapsible = this.collapsibles.get(element)
      if (collapsible && collapsible.isExpanded) {
        return this.lastExpandedChildOrSelv(element.children[element.children.length - 1]) || element
      }
      return element
    },
    down () {
      this.selectNode(this.ancestorsNextSibling(this.selectedNode))
    },
    ancestorsNextSibling (element) {
      if (!element) {
        return
      }

      if (element.nextElementSibling) {
        return element.nextElementSibling
      } else {
        return this.ancestorsNextSibling(element.parentNode)
      }
    },
    goIn () {
      if (this.selectedNode && this.selectedNode.children.length) {
        this.selectNode(this.selectedNode.children[0])
      } else {
        this.down()
      }
    },
    contains (node) {
      if (this.source.contains) {
        return this.source.contains(node)
      } else {
        return contains(this.source, node)
      }
    }
  },
  render (createElement) {
    const {virtual} = this

    if (!virtual) {
      return
    }

    this.refs = new Map()

    return createElement(
      'div',
      {
        class: 'source-view',
        on: {
          keydown: this.onKeyDown
        }
      },
      [
        render(
          this.virtual,
          this.refs,
          createElement,
          {
            selectedNode: this.selectedNode,
            collapsibles: this.collapsibles,
            expandMap: this.expandMap
          },
          {
            selectNode: this.selectNode,
            onToggle: () => {
              this.$forceUpdate()
              // force the focus by 'selecting' again:
              this.selectNode(this.selectedNode)
              this.highlight()
            }
          }),
        this.$slots.default
      ]
    )
  },
  updated () {
    this.highlight()
  }
})

/**
 * @see https://www.w3.org/TR/html51/syntax.html#void-elements
 */
const VOID = {
  area: true,
  base: true,
  br: true,
  col: true,
  embed: true,
  hr: true,
  img: true,
  input: true,
  keygen: true,
  link: true,
  menuitem: true,
  meta: true,
  param: true,
  source: true,
  track: true,
  wbr: true
}

const NEWLINE = /\r\n+|\r+|\n+/

const WHITESPACE_LEFT = /^\s+/

const WHITESPACE_RIGHT = /\s+$/

function serialize (node) {
  switch (node.nodeType) {
    // https://w3c.github.io/DOM-Parsing/#xml-serializing-a-documenttype-node
    case node.DOCUMENT_TYPE_NODE:
      return `<!DOCTYPE ${node.name}` +
        (node.publicId ? ` PUBLIC "${node.publicId}"` : node.systemId ? ' SYSTEM' : '') +
        (node.systemId ? ` "${node.systemId}"` : '') +
        '>'
    default:
      return node.outerHTML
  }
}

function tag (element) {
  return element.tagName.toLowerCase()
}

function trimLeft (string) {
  return string.replace(WHITESPACE_LEFT, '')
}

function trimRight (string) {
  return string.replace(WHITESPACE_RIGHT, '')
}

function first (array) {
  return array[0]
}

function last (array) {
  return array[array.length - 1]
}

function children (node) {
  switch (node.nodeType) {
    case node.ELEMENT_NODE:
      return tag(node) === 'template' && 'content' in node
        ? [node.content]
        : [...node.childNodes]
    case node.DOCUMENT_NODE:
    case node.DOCUMENT_FRAGMENT_NODE:
      return [...node.childNodes]
    default:
      return []
  }
}

function virtualize (node) {
  switch (node.nodeType) {
    case node.ELEMENT_NODE:
      const childNodes = group(children(node).map(c => virtualize(c)))
      const isSingleLine = childNodes.length <= 1 && childNodes.every(c =>
        c.type === 'text' &&
        c.lines.length <= 1 &&
        c.value.trim().length <= 20
      )
      const elemTag = tag(node)
      return {
        type: 'element',
        ref: node,
        tag: elemTag,
        base: node.baseURI,
        children: childNodes,
        isSingleLine,
        isVoid: elemTag in VOID,
        attributes: [...node.attributes].map(attribute => ({
          name: attribute.name,
          value: attribute.value,
          ref: attribute
        }))
      }
    case node.TEXT_NODE:
      return {
        type: 'text',
        value: node.nodeValue,
        lines: node.nodeValue.split(NEWLINE).map(line => ({
          value: line,
          fragments: [{value: line, ref: node}]
        })),
        onlyWhitespace: node.nodeValue.trim() === ''
      }
    case node.COMMENT_NODE:
      return {
        type: 'comment',
        value: node.nodeValue
      }
    case node.DOCUMENT_NODE:
      return {
        type: 'document',
        children: group(children(node).map(c => virtualize(c, 0)))
      }
    case node.DOCUMENT_TYPE_NODE:
      return {
        type: 'type',
        value: serialize(node)
      }
    case node.DOCUMENT_FRAGMENT_NODE:
      return {
        type: 'fragment',
        children: group(children(node).map(c => virtualize(c)))
      }
  }
}

function group (children) {
  const grouped = []

  children.forEach(child => {
    const prev = grouped[grouped.length - 1]

    switch (child.type) {
      case 'text':
        if (prev && prev.type === 'text') {
          const prevLine = prev.lines[prev.lines.length - 1]
          const nextLine = child.lines.shift()

          prevLine.value += nextLine.value
          prevLine.fragments = [...prevLine.fragments, ...nextLine.fragments]

          if (child.lines.length === 0) {
            return
          }
        }
    }

    grouped.push(child)
  })

  grouped.forEach(child => {
    switch (child.type) {
      case 'text':
        child.lines = child.lines.filter(line =>
          line.value.trim() !== ''
        )

        child.value = child.lines.map(line => line.value).join('')

        if (child.lines.length !== 0) {
          const fst = first(first(child.lines).fragments)
          const lst = last(last(child.lines).fragments)

          fst.value = trimLeft(fst.value)
          lst.value = trimRight(lst.value)
        }
    }
  })

  return grouped
}

function clean (node) {
  switch (node.type) {
    case 'element':
    case 'document':
    case 'fragment':
      node.children = node.children
        .filter(c => c !== undefined)
        .map(c => clean(c))
  }

  return node
}

function lineNumber (node, numberRef) {
  if (numberRef === undefined) {
    numberRef = { ln: 1 }
  }

  if (!node.onlyWhitespace) {
    node.lineNumber = numberRef.ln++
    if (!node.isSingleLine) {
      node.children && node.children.forEach(c => {
        lineNumber(c, numberRef)
      })
    }
  }

  return node
}

function render (node, refs, createElement, state, actions) {
  switch (node.type) {
    case 'element':
      return element(node, refs, createElement, state, actions)
    case 'text':
      return text(node, refs, createElement)
    case 'comment':
      return comment(node, createElement)
    case 'document':
      return document(node, refs, createElement, state, actions)
    case 'type':
      return type(node, createElement)
    case 'fragment':
      return fragment(node, refs, createElement, state, actions)
  }
}

function onElementClick (e, node, selectNode) {
  e.stopPropagation()
  selectNode(node)
}

function element (node, refs, createElement, state, actions) {
  const {children, isSingleLine, isVoid, lineNumber} = node
  const {selectNode, onToggle} = actions

  const isSelected = node.ref === state.selectedNode

  const isCollapsible = !isVoid && !isSingleLine

  const openingTag = tagStart(node, refs, createElement, isSelected && isCollapsible)
  const closingTag = tagEnd(node, createElement)
  const childNodes = () => children.map(c => render(c, refs, createElement, state, actions))

  const element = createElement(
    'div',
    {
      class: {
        'source-view-element': true,
        'is-selected': isSelected,
        'is-void': isVoid,
        'is-single-line': isSingleLine
      },
      on: {
        click: e => {
          onElementClick(e, node.ref, selectNode)
        }
      },
      attrs: {
        tabindex: isSelected && !isCollapsible ? '0' : undefined
      }
    },
    isCollapsible
      ? collapse(createElement, node.ref, state, onToggle, [openingTag, closingTag], childNodes, lineNumber)
      : isVoid
        ? [openingTag]
        : [openingTag, ...childNodes(), closingTag]
  )

  refs.set(node.ref, element)

  return element
}

function collapse (createElement, node, state, onToggle, content, childNodes, lineNumber) {
  return [createElement(
    SourceViewCollapse, {
      props: {
        node,
        state,
        onToggle,
        content,
        childNodes,
        lineNumber
      }
    }
  )]
}

const SourceViewVNodes = Component('v-vnodes', {
  props: {
    nodes: [Object, Array]
  },
  render (h) {
    const {nodes} = this
    return h('span', {}, Array.isArray(nodes) ? [this.$slots.default, ...nodes] : [this.$slots.default, nodes])
  }
})

export const SourceViewCollapse = Component('v-source-view-collapse', {
  props: {
    content: Array,
    childNodes: Function,
    node: {
      default: null
    },
    state: Object,
    onToggle: Function,
    lineNumber: {
      type: Number,
      default: -1
    }
  },
  mounted () {
    this.state.collapsibles.set(this.node, this)
  },
  computed: {
    tagStart () {
      return this.content[0]
    },
    tagEnd () {
      return this.content[this.content.length - 1]
    },
    children () {
      return this.isExpanded
        ? this.childNodes()
        : []
    },
    isExpanded () {
      return this.state.expandMap.get(this.node)
    }
  },
  methods: {
    expand () {
      this.state.expandMap.set(this.node, true)
      this.onToggle()
    },
    toggle () {
      this.state.expandMap.set(this.node, !this.isExpanded)
      this.onToggle()
    },
    focus () {
      if (this.tagStart && this.tagStart.elm) {
        this.tagStart.elm.focus()
        return this.tagStart.elm
      }
    }
  },
  template: SourceViewCollapseTemplate,
  components: [SourceViewVNodes]
})

function text (node, refs, createElement) {
  if (node.value.trim() === '') {
    return undefined
  } else {
    const text = createElement(
      'div',
      {
        class: 'source-view-text'
      },
      node.lines.map(l => textLine(node, l, refs, createElement))
    )
    return text
  }
}

function textLine (text, line, refs, createElement) {
  return createElement(
    'div',
    {
      class: 'source-view-text-line'
    },
    [
      ...line.fragments.map(f => textFragment(f, refs, createElement))
    ]
  )
}

function textFragment (fragment, refs, createElement) {
  const element = createElement(
    'span',
    {
      class: 'source-view-text-fragment'
    },
    fragment.value
  )

  refs.set(fragment.ref, element)

  return element
}

function comment (node, createElement) {
  return createElement(
    'div',
    {
      class: 'source-view-comment'
    },
    [
      '<!--', node.value, '-->'
    ]
  )
}

function document (node, refs, createElement, state, actions) {
  return createElement(
    'div',
    {
      class: 'source-view-document'
    },
    node.children.map(c => render(c, refs, createElement, state, actions))
  )
}

function type (node, createElement) {
  return createElement(
    'div',
    {
      class: 'source-view-type'
    },
    [
      node.value
    ]
  )
}

function fragment (node, refs, createElement, state, actions) {
  return createElement(
    'div',
    {
      class: 'source-view-fragment'
    },
    node.children.map(c => render(c, refs, createElement, state, actions))
  )
}

function tagStart (element, refs, createElement, isFocusable = false) {
  return createElement(
    'span',
    {
      class: 'source-view-tag',
      attrs: {
        tabindex: isFocusable ? '0' : undefined
      }
    },
    [
      '<',
      tagName(element, createElement),
      ...element.attributes
        .map(a => [
          ' ',
          attribute(a, refs, createElement)]
        )
        .reduce((a, b) => a.concat(b), []),
      '>'
    ]
  )
}

function tagEnd (element, createElement) {
  return createElement(
    'span',
    {
      class: 'source-view-tag'
    },
    [
      '</',
      tagName(element, createElement),
      '>'
    ]
  )
}

function tagName (element, createElement) {
  return createElement(
    'span',
    {
      class: 'source-view-tag-name'
    },
    [
      element.tag
    ]
  )
}

function attribute ({name, value, ref}, refs, createElement) {
  const attribute = createElement(
    'span',
    {
      class: 'source-view-attribute'
    },
    [
      attributeName({name}, createElement),
      value !== '' && [
        '="',
        attributeValue({name, value}, createElement),
        '"'
      ]
    ]
  )

  refs.set(ref, attribute)

  return attribute
}

function attributeName ({name}, createElement) {
  return createElement(
    'span',
    {
      class: 'source-view-attribute-name'
    },
    [
      name
    ]
  )
}

function attributeValue ({name, value}, createElement) {
  return createElement(
    'span',
    {
      class: 'source-view-attribute-value'
    },
    [
      value
    ]
  )
}

function elementFromNode (node) {
  if (!node) {
    return
  }
  switch (node.nodeType) {
    case node.ELEMENT_NODE:
      return node
    case node.ATTRIBUTE_NODE:
      return node.ownerElement
    case node.TEXT_NODE:
      return node.parentNode
    case node.DOCUMENT_NODE:
      return [...node.childNodes].filter(n => n.nodeType === node.ELEMENT_NODE)[0]
  }
}

// IE polyfill
// inspired by: https://github.com/Financial-Times/polyfill-library/blob/master/polyfills/Node/prototype/contains/polyfill.js
function contains (node, otherNode) {
  if (!node || !otherNode) {
    return false
  }

  while (otherNode) {
    if (otherNode === node) {
      return true
    }
    otherNode = otherNode.parentNode
  }

  return false
}
